import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TableSortLabel,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { EmojiEvents, TrendingUp } from "@mui/icons-material";
import { supabase } from "../lib/supabaseClient";
import DashboardLayout from "../components/DashboardLayout";
import ProviderBadge from "../components/ProviderBadge";

// =============================================
// MOCK DATA TOGGLE - Set to false to disable
// =============================================
const MOCK_DATA_ENABLED = false;

// Mock data generator for testing tab switching
const generateMockData = () => {
  const mockCategories = {
    XSS: [
      {
        id: "mock-xss-1",
        title: "Cross-Site Scripting in User Profile",
        author: "Test User",
        createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)),
        providerName: "OpenAI",
        category: "XSS",
      },
      {
        id: "mock-xss-2",
        title: "Reflected XSS in Search Parameter",
        author: "Anonymous",
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)),
        providerName: "Anthropic",
        category: "XSS",
      },
      {
        id: "mock-xss-3",
        title: "Stored XSS in Comment Section",
        author: "Security Researcher",
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)),
        providerName: null,
        category: "XSS",
      },
    ],
    "SQL Injection": [
      {
        id: "mock-sql-1",
        title: "SQL Injection in Login Form",
        author: "Test User",
        createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
        providerName: "Google",
        category: "SQL Injection",
      },
      {
        id: "mock-sql-2",
        title: "Blind SQL Injection in Search",
        author: "Anonymous",
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)),
        providerName: "OpenAI",
        category: "SQL Injection",
      },
    ],
    CSRF: [
      {
        id: "mock-csrf-1",
        title: "CSRF Token Bypass in Account Settings",
        author: "Test User",
        createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)),
        providerName: "Anthropic",
        category: "CSRF",
      },
      {
        id: "mock-csrf-2",
        title: "Missing CSRF Protection on Password Change",
        author: "Anonymous",
        createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 4 * 24 * 60 * 60 * 1000)),
        providerName: null,
        category: "CSRF",
      },
    ],
    "Authentication Bypass": [
      {
        id: "mock-auth-1",
        title: "Authentication Bypass via Direct URL Access",
        author: "Security Researcher",
        createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 8 * 24 * 60 * 60 * 1000)),
        providerName: "Google",
        category: "Authentication Bypass",
      },
      {
        id: "mock-auth-2",
        title: "JWT Token Manipulation Vulnerability",
        author: "Test User",
        createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 1 * 60 * 60 * 1000)),
        providerName: "OpenAI",
        category: "Authentication Bypass",
      },
    ],
    "Insecure Deserialization": [
      {
        id: "mock-deser-1",
        title: "Insecure Deserialization in API Endpoint",
        author: "Anonymous",
        createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
        date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)),
        providerName: "Anthropic",
        category: "Insecure Deserialization",
      },
    ],
  };

  return mockCategories;
};

// Comparators for sorting
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Cache to store leaderboard data
const leaderboardCache = {
  categories: null,
  data: {},
  lastFetch: null,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
};

// Internal component that receives 'user' from DashboardLayout
const LeaderboardContent = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState({});
  const [loadingCategories, setLoadingCategories] = useState(new Set());
  const [activeTab, setActiveTab] = useState(0);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Fetch list of unique categories
  const fetchCategoryList = useCallback(async () => {
    const { data, error } = await supabase
      .from("issue_reports")
      .select("type_of_report")
      .eq("publication_status", "published");

    if (error) {
      throw new Error(`Error fetching categories: ${error.message}`);
    }

    // Get unique categories client-side (Supabase doesn't support distinct in select)
    const categorySet = new Set(
      data.map((r) => (r.type_of_report || "Uncategorized").trim())
    );

    // Add mock categories if enabled
    if (MOCK_DATA_ENABLED) {
      const mockData = generateMockData();
      Object.keys(mockData).forEach((category) => categorySet.add(category));
    }

    // Sort categories alphabetically, but put "Uncategorized" last
    const sortedCategories = Array.from(categorySet).sort((a, b) => {
      if (a === "Uncategorized") return 1;
      if (b === "Uncategorized") return -1;
      return a.localeCompare(b);
    });

    return sortedCategories;
  }, []);

  // Fetch data for a single category with server-side filtering
  const fetchCategoryData = useCallback(
    async (category) => {
      // Check if already in state
      if (leaderboardData[category]) {
        return;
      }

      // Check if in cache (and we are just missing it in local state)
      if (leaderboardCache.data[category]) {
        setLeaderboardData((prev) => ({
          ...prev,
          [category]: leaderboardCache.data[category],
        }));
        return;
      }

      // If mock data is enabled and this is a mock category, return mock data
      if (MOCK_DATA_ENABLED) {
        const mockData = generateMockData();
        if (mockData[category]) {
          // Simulate network delay for realistic testing
          await new Promise((resolve) => setTimeout(resolve, 300));

          const mocked = mockData[category];

          // Update State
          setLeaderboardData((prev) => ({
            ...prev,
            [category]: mocked,
          }));

          // Update Cache
          leaderboardCache.data[category] = mocked;
          return;
        }
      }

      // Mark as loading
      setLoadingCategories((prev) => new Set(prev).add(category));

      try {
        // Fetch reports for this specific category only
        const { data: reports, error: reportsError } = await supabase
          .from("issue_reports")
          .select("id, report_title, is_anonymous, user_id, created_at, org_id")
          .eq("publication_status", "published")
          .eq("type_of_report", category === "Uncategorized" ? null : category)
          .order("created_at", { ascending: false });

        if (reportsError) {
          throw new Error(
            `Error fetching category data: ${reportsError.message}`
          );
        }

        if (!reports || reports.length === 0) {
          const empty = [];
          setLeaderboardData((prev) => ({
            ...prev,
            [category]: empty,
          }));
          leaderboardCache.data[category] = empty;
          return;
        }

        // Fetch user profiles for this category's reports
        const userIds = [...new Set(reports.map((report) => report.user_id))];
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, username, full_name")
          .in("id", userIds);

        if (profilesError) {
          console.error("Error fetching profiles:", profilesError);
        }

        const profileMap = new Map(
          (profiles || []).map((profile) => [profile.id, profile])
        );

        // Fetch organizations for this category's reports
        const orgIds = [
          ...new Set(reports.map((r) => r.org_id).filter(Boolean)),
        ];
        let orgMap = new Map();
        if (orgIds.length > 0) {
          const { data: orgs, error: orgsError } = await supabase
            .from("organizations")
            .select("id, name")
            .in("id", orgIds);

          if (!orgsError && orgs) {
            orgMap = new Map(orgs.map((o) => [o.id, o.name]));
          }
        }

        // Process reports into display format
        const processedReports = reports.map((report) => {
          // Get author name
          let authorName = "Anonymous";
          if (!report.is_anonymous && report.user_id) {
            const profile = profileMap.get(report.user_id);
            if (profile) {
              authorName =
                profile.full_name ||
                profile.username ||
                `User ${report.user_id.substring(0, 6)}`;
            } else {
              authorName = `User ${report.user_id.substring(0, 6)}`;
            }
          }

          return {
            id: report.id,
            title: report.report_title || "(Untitled Report)",
            author: authorName,
            createdAt: new Date(report.created_at),
            date: new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }).format(new Date(report.created_at)),
            providerName: report.org_id ? orgMap.get(report.org_id) : null,
            category: category,
          };
        });

        // Cache the processed data
        setLeaderboardData((prev) => ({
          ...prev,
          [category]: processedReports,
        }));
        leaderboardCache.data[category] = processedReports;
      } catch (err) {
        console.error(`Error fetching category "${category}":`, err);
      } finally {
        // Remove from loading set
        setLoadingCategories((prev) => {
          const newSet = new Set(prev);
          newSet.delete(category);
          return newSet;
        });
      }
    },
    [leaderboardData]
  );

  // Prefetch multiple categories in background
  const prefetchCategories = useCallback(
    async (categoryList) => {
      // 1. Filter out already cached/loading categories
      const categoriesToFetch = categoryList.filter(
        (category) =>
          !leaderboardData[category] && !leaderboardCache.data[category]
      );

      // 2. Use Promise.all to fetch them simultaneously
      await Promise.all(
        categoriesToFetch.map((category) => fetchCategoryData(category))
      );
    },
    [leaderboardData, fetchCategoryData]
  );

  // Initialize: Fetch categories and load first category
  const initializeLeaderboard = useCallback(async () => {
    try {
      setError(null);

      const now = Date.now();
      const isCacheValid =
        leaderboardCache.categories &&
        leaderboardCache.lastFetch &&
        now - leaderboardCache.lastFetch < leaderboardCache.CACHE_DURATION;

      if (isCacheValid) {
        // Restore from cache
        setCategories(leaderboardCache.categories);
        // Important: Restore data for the first category (or all) so the view isn't empty
        setLeaderboardData(leaderboardCache.data);
        setLoading(false);
        return;
      } else {
        setLoading(true);
      }

      // Step 1: Fetch unique categories
      const categoryList = await fetchCategoryList();

      if (categoryList.length === 0) {
        setCategories([]);
        setLeaderboardData({});
        setLoading(false);
        return;
      }

      setCategories(categoryList);

      // Update Cache Metadata
      leaderboardCache.categories = categoryList;
      leaderboardCache.lastFetch = Date.now();

      // Step 2: Load first category's data immediately
      await fetchCategoryData(categoryList[0]);

      setLoading(false);

      // Step 3: Prefetch next categories in background (after short delay)
      setTimeout(() => {
        const categoriesToPrefetch = categoryList.slice(1);
        prefetchCategories(categoriesToPrefetch);
      }, 500);
    } catch (err) {
      console.error("Error initializing leaderboard:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [fetchCategoryList, fetchCategoryData, prefetchCategories]);

  useEffect(() => {
    initializeLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle tab change
  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue);

    const selectedCategory = categories[newValue];

    // Fetch data if not cached
    if (!leaderboardData[selectedCategory]) {
      await fetchCategoryData(selectedCategory);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Failed to load leaderboard: {error}</Alert>
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">
          No published reports available yet. Check back later!
        </Alert>
      </Box>
    );
  }

  const currentCategory = categories[activeTab];
  const currentData = leaderboardData[currentCategory] || [];

  // Apply sorting
  const visibleRows = stableSort(currentData, getComparator(order, orderBy));

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <Box
        component="div"
        sx={{ flexGrow: 1, p: 4, minWidth: 0, maxWidth: "100%" }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 4, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <EmojiEvents
              sx={{
                fontSize: 40,
                color: "primary.main",
                opacity: 0.9,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                minWidth: 0,
                wordBreak: "break-word",
              }}
            >
              Security Vulnerability Leaderboard
            </Typography>
          </Box>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ ml: { xs: 0, sm: 7 }, minWidth: 0 }}
          >
            Explore security reports organized by vulnerability type. Rankings
            coming soon!
          </Typography>
        </Box>

        {/* Category Tabs */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 3,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                minWidth: 80,
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.05),
                },
              },
              "& .Mui-selected": {
                color: "primary.main",
              },
            }}
          >
            {categories.map((category, index) => (
              <Tab
                key={category}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>{category}</span>
                    <Chip
                      label={leaderboardData[category]?.length || 0}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        bgcolor: (theme) =>
                          activeTab === index
                            ? theme.palette.primary.main
                            : alpha(theme.palette.primary.main, 0.1),
                        color: (theme) =>
                          activeTab === index
                            ? theme.palette.primary.contrastText
                            : theme.palette.primary.main,
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Leaderboard Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            maxWidth: "100%",
            overflowX: "auto",
          }}
        >
          <Table sx={{ width: "100%" }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                }}
              >
                <TableCell
                  sortDirection={orderBy === "title" ? order : false}
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  <TableSortLabel
                    active={orderBy === "title"}
                    direction={orderBy === "title" ? order : "asc"}
                    onClick={(event) => handleRequestSort(event, "title")}
                  >
                    Report Title
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "providerName" ? order : false}
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  <TableSortLabel
                    active={orderBy === "providerName"}
                    direction={orderBy === "providerName" ? order : "asc"}
                    onClick={(event) =>
                      handleRequestSort(event, "providerName")
                    }
                  >
                    Provider
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "author" ? order : false}
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  <TableSortLabel
                    active={orderBy === "author"}
                    direction={orderBy === "author" ? order : "asc"}
                    onClick={(event) => handleRequestSort(event, "author")}
                  >
                    Author
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "createdAt" ? order : false}
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  <TableSortLabel
                    active={orderBy === "createdAt"}
                    direction={orderBy === "createdAt" ? order : "asc"}
                    onClick={(event) => handleRequestSort(event, "createdAt")}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingCategories.has(currentCategory) ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Loading {currentCategory} reports...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : currentData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No reports in this category yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.03),
                        transform: "scale(1.001)",
                      },
                      "&:last-child td": {
                        border: 0,
                      },
                    }}
                  >
                    {/* Report Title */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.title}
                      </Typography>
                    </TableCell>

                    {/* Provider */}
                    <TableCell>
                      {item.providerName ? (
                        <ProviderBadge providerName={item.providerName} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Author */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.author}
                      </Typography>
                    </TableCell>

                    {/* Date */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.date}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Stats Footer */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TrendingUp sx={{ color: "primary.main", fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary">
            Showing <strong>{visibleRows.length}</strong> reports in{" "}
            <strong>{currentCategory}</strong> category
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const LeaderboardPage = () => {
  return (
    <DashboardLayout>
      <LeaderboardContent />
    </DashboardLayout>
  );
};

export default LeaderboardPage;
